import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import DashboardLayout from '../components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Upload, Download, FileText, Trash2 } from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function DocumentsPage() {
  const { user } = useAuth();
  const { t, lang } = useLanguage();
  const [documents, setDocuments] = useState([]);
  const [cases, setCases] = useState([]);
  const [selectedCase, setSelectedCase] = useState('all');
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  const fetchData = useCallback(async () => {
    try {
      const [docsRes, casesRes] = await Promise.all([
        axios.get(`${API}/documents${selectedCase !== 'all' ? `?case_id=${selectedCase}` : ''}`, { withCredentials: true }),
        axios.get(`${API}/cases`, { withCredentials: true }),
      ]);
      setDocuments(docsRes.data);
      setCases(casesRes.data);
    } catch {}
  }, [selectedCase]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleUpload = async (e) => {
    const files = e.target.files;
    if (!files?.length || selectedCase === 'all') return;
    setUploading(true);
    for (const file of files) {
      const formData = new FormData();
      formData.append('file', file);
      try {
        await axios.post(`${API}/documents/upload?case_id=${selectedCase}`, formData, {
          withCredentials: true,
          headers: { 'Content-Type': 'multipart/form-data' }
        });
      } catch {}
    }
    setUploading(false);
    fetchData();
  };

  const handleDownload = async (doc) => {
    try {
      const res = await axios.get(`${API}/documents/${doc.doc_id}/download`, {
        withCredentials: true,
        responseType: 'blob'
      });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = doc.original_filename;
      a.click();
      URL.revokeObjectURL(url);
    } catch {}
  };

  const handleDelete = async (docId) => {
    if (!window.confirm(lang === 'ar' ? 'هل أنت متأكد؟' : 'Are you sure?')) return;
    try {
      await axios.delete(`${API}/documents/${docId}`, { withCredentials: true });
      fetchData();
    } catch {}
  };

  const isAdminOrLawyer = user?.role === 'admin' || user?.role === 'lawyer';

  return (
    <DashboardLayout>
      <div data-testid="documents-page">
        <div className="flex items-center justify-between mb-8">
          <h1 className="text-2xl sm:text-3xl font-bold text-[#0F172A] font-heading">{t('documents')}</h1>
          <div className="flex items-center gap-3">
            <Select value={selectedCase} onValueChange={setSelectedCase}>
              <SelectTrigger className="w-48" data-testid="case-filter-select">
                <SelectValue placeholder={t('filter')} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{lang === 'ar' ? 'جميع القضايا' : 'All Cases'}</SelectItem>
                {cases.map(c => <SelectItem key={c.case_id} value={c.case_id}>{c.title}</SelectItem>)}
              </SelectContent>
            </Select>
            {selectedCase !== 'all' && (
              <>
                <input type="file" ref={fileInputRef} onChange={handleUpload} className="hidden" multiple data-testid="docs-page-file-input" />
                <Button
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploading}
                  className="bg-[#B48E4B] text-white hover:bg-[#8C6A32]"
                  data-testid="docs-page-upload-button"
                >
                  <Upload className="w-4 h-4 me-2" /> {uploading ? '...' : t('upload_document')}
                </Button>
              </>
            )}
          </div>
        </div>

        {documents.length > 0 ? (
          <Card className="border-slate-200 shadow-sm">
            <Table>
              <TableHeader>
                <TableRow className="border-b border-slate-100">
                  <TableHead className="text-xs uppercase tracking-wider text-[#94A3B8]">{lang === 'ar' ? 'الملف' : 'File'}</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider text-[#94A3B8]">{t('case_title')}</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider text-[#94A3B8]">{lang === 'ar' ? 'الحجم' : 'Size'}</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider text-[#94A3B8]">{lang === 'ar' ? 'رفع بواسطة' : 'Uploaded By'}</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider text-[#94A3B8]">{lang === 'ar' ? 'التاريخ' : 'Date'}</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider text-[#94A3B8]"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {documents.map(doc => {
                  const caseTitle = cases.find(c => c.case_id === doc.case_id)?.title || doc.case_id?.slice(0, 12);
                  return (
                    <TableRow key={doc.doc_id} className="border-b border-slate-100" data-testid={`doc-row-${doc.doc_id}`}>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <FileText className="w-4 h-4 text-[#B48E4B]" />
                          <span className="font-medium text-[#0F172A] text-sm">{doc.original_filename}</span>
                        </div>
                      </TableCell>
                      <TableCell className="text-sm text-[#475569]">{caseTitle}</TableCell>
                      <TableCell className="text-sm text-[#475569]">{(doc.size / 1024).toFixed(1)} KB</TableCell>
                      <TableCell className="text-sm text-[#475569]">{doc.uploader_name}</TableCell>
                      <TableCell className="text-sm text-[#94A3B8]">{new Date(doc.created_at).toLocaleDateString()}</TableCell>
                      <TableCell>
                        <div className="flex gap-1">
                          <button onClick={() => handleDownload(doc)} className="p-1.5 rounded hover:bg-slate-100 text-[#475569]" data-testid={`download-${doc.doc_id}`}>
                            <Download className="w-4 h-4" />
                          </button>
                          {isAdminOrLawyer && (
                            <button onClick={() => handleDelete(doc.doc_id)} className="p-1.5 rounded hover:bg-red-50 text-red-500" data-testid={`delete-${doc.doc_id}`}>
                              <Trash2 className="w-4 h-4" />
                            </button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </Card>
        ) : (
          <Card className="border-slate-200 shadow-sm">
            <CardContent className="p-12 text-center">
              <FileText className="w-12 h-12 text-[#94A3B8] mx-auto mb-4" />
              <p className="text-[#94A3B8]">{t('no_documents')}</p>
            </CardContent>
          </Card>
        )}
      </div>
    </DashboardLayout>
  );
}
